// Country flag emoji from a league's country code — ported from Catalyst's
// soccer-frontend/src/lib/country-flag.ts (kept in sync so the picker matches
// the live app). Pure, DOM-free, unit-tested.
//
// The DB `countryCode` is ISO 3166-1 alpha-3 (or a FIFA/custom code like ENG).
// We map alpha-3 → alpha-2, then alpha-2 → the regional-indicator emoji; the GB
// home nations use tag-sequence flags.

const ALPHA3_TO_ALPHA2: Record<string, string> = {
  // Africa
  DZA: "dz", AGO: "ao", BEN: "bj", BWA: "bw", BFA: "bf", BDI: "bi",
  CPV: "cv", CMR: "cm", CAF: "cf", TCD: "td", COM: "km", COG: "cg",
  COD: "cd", CIV: "ci", DJI: "dj", EGY: "eg", GNQ: "gq", ERI: "er",
  SWZ: "sz", ETH: "et", GAB: "ga", GMB: "gm", GHA: "gh", GIN: "gn",
  GNB: "gw", KEN: "ke", LSO: "ls", LBR: "lr", LBY: "ly", MDG: "mg",
  MWI: "mw", MLI: "ml", MRT: "mr", MUS: "mu", MYT: "yt", MAR: "ma",
  MOZ: "mz", NAM: "na", NER: "ne", NGA: "ng", REU: "re", RWA: "rw",
  SHN: "sh", STP: "st", SEN: "sn", SYC: "sc", SLE: "sl", SOM: "so",
  ZAF: "za", SSD: "ss", SDN: "sd", TZA: "tz", TGO: "tg", TUN: "tn",
  UGA: "ug", ESH: "eh", ZMB: "zm", ZWE: "zw",
  // Americas
  AIA: "ai", ATG: "ag", ARG: "ar", ABW: "aw", BHS: "bs", BRB: "bb",
  BLZ: "bz", BMU: "bm", BOL: "bo", BES: "bq", BRA: "br", VGB: "vg",
  CAN: "ca", CYM: "ky", CHL: "cl", COL: "co", CRI: "cr", CUB: "cu",
  CUW: "cw", DMA: "dm", DOM: "do", ECU: "ec", SLV: "sv", FLK: "fk",
  GUF: "gf", GRD: "gd", GLP: "gp", GTM: "gt", GUY: "gy", HTI: "ht",
  HND: "hn", JAM: "jm", MTQ: "mq", MEX: "mx", MSR: "ms", NIC: "ni",
  PAN: "pa", PRY: "py", PER: "pe", PRI: "pr", KNA: "kn", LCA: "lc",
  VCT: "vc", SUR: "sr", TTO: "tt", TCA: "tc", VIR: "vi", URY: "uy",
  VEN: "ve", GRL: "gl", USA: "us",
  // Asia
  AFG: "af", ARM: "am", AZE: "az", BHR: "bh", BGD: "bd", BTN: "bt",
  BRN: "bn", KHM: "kh", CHN: "cn", CYP: "cy", GEO: "ge", HKG: "hk",
  IND: "in", IDN: "id", IRN: "ir", IRQ: "iq", ISR: "il", JPN: "jp",
  JOR: "jo", KAZ: "kz", PRK: "kp", KOR: "kr", KWT: "kw", KGZ: "kg",
  LAO: "la", LBN: "lb", MAC: "mo", MYS: "my", MDV: "mv", MNG: "mn",
  MMR: "mm", NPL: "np", OMN: "om", PAK: "pk", PSE: "ps", PHL: "ph",
  QAT: "qa", SAU: "sa", SGP: "sg", LKA: "lk", SYR: "sy", TWN: "tw",
  TJK: "tj", THA: "th", TLS: "tl", TKM: "tm", ARE: "ae", UZB: "uz",
  VNM: "vn", YEM: "ye",
  // Europe
  ALA: "ax", ALB: "al", AND: "ad", AUT: "at", BLR: "by", BEL: "be",
  BIH: "ba", BGR: "bg", HRV: "hr", CZE: "cz", DNK: "dk", EST: "ee",
  FRO: "fo", FIN: "fi", FRA: "fr", DEU: "de", GIB: "gi", GRC: "gr",
  GGY: "gg", HUN: "hu", ISL: "is", IRL: "ie", IMN: "im", ITA: "it",
  JEY: "je", LVA: "lv", LIE: "li", LTU: "lt", LUX: "lu", MLT: "mt",
  MDA: "md", MCO: "mc", MNE: "me", NLD: "nl", MKD: "mk", NOR: "no",
  POL: "pl", PRT: "pt", ROU: "ro", RUS: "ru", SMR: "sm", SRB: "rs",
  SVK: "sk", SVN: "si", ESP: "es", SWE: "se", CHE: "ch", UKR: "ua",
  GBR: "gb", VAT: "va", TUR: "tr",
  // Oceania
  ASM: "as", AUS: "au", COK: "ck", FJI: "fj", PYF: "pf", GUM: "gu",
  KIR: "ki", MHL: "mh", FSM: "fm", NRU: "nr", NCL: "nc", NZL: "nz",
  NIU: "nu", MNP: "mp", PLW: "pw", PNG: "pg", WSM: "ws", SLB: "sb",
  TON: "to", TUV: "tv", VUT: "vu",
  // Non-standard / FIFA codes used in golden.country
  ENG: "gb-eng", SCO: "gb-sct", WAL: "gb-wls", NOI: "gb-nir", XK: "xk",
};

const SUBDIVISION_FLAGS: Record<string, string> = {
  "gb-eng": "🏴󠁧󠁢󠁥󠁮󠁧󠁿",
  "gb-sct": "🏴󠁧󠁢󠁳󠁣󠁴󠁿",
  "gb-wls": "🏴󠁧󠁢󠁷󠁬󠁳󠁿",
  "gb-nir": "🇬🇧",
};

function alpha2ToEmoji(alpha2: string): string {
  if (alpha2 in SUBDIVISION_FLAGS) return SUBDIVISION_FLAGS[alpha2]!;
  // Regional Indicator A = U+1F1E6; offset from 'A' (65) = 127397.
  return String.fromCodePoint(
    ...[...alpha2.toUpperCase()].map((c) => 127397 + c.charCodeAt(0))
  );
}

/** Flag emoji for a league's country code (ISO alpha-3 / FIFA code), or "" when
 *  there's no code or no mapping (e.g. supranational competitions). */
export function countryFlag(alpha3: string | null | undefined): string {
  if (!alpha3) return "";
  const alpha2 = ALPHA3_TO_ALPHA2[alpha3.toUpperCase()];
  return alpha2 ? alpha2ToEmoji(alpha2) : "";
}
